/**
 * The Apollo client the document's providers get (web plan W4.3).
 *
 * In the browser: the app's persisted singleton (`getApolloClient`). On the
 * server, never that client (W4.2: the server never creates or uses it) but
 * an inert one per render that answers nothing, as the W4.2 server-render
 * harness does: the pages' queries render their loading state, the same first
 * render the browser hydrates (the persisted cache is restored only in the
 * first commit's effects, PersistedCacheRestore). Server-rendered bodies come
 * from route loaders (W4.4 / W4.5), not from Apollo.
 */
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  Observable,
  type NormalizedCacheObject,
} from '@apollo/client';
import { cacheTypePolicies, getApolloClient } from '@/lib/apollo';

/** A client whose link never answers, in SSR mode. A fresh one per call: nothing outlives the render. */
export function createInertApolloClient(): ApolloClient<NormalizedCacheObject> {
  return new ApolloClient({
    cache: new InMemoryCache({ typePolicies: cacheTypePolicies }),
    link: new ApolloLink(() => new Observable(() => {})),
    ssrMode: true,
  });
}

/** The browser's singleton, or (no `window`) a fresh inert client. */
export function documentApolloClient(): ApolloClient<NormalizedCacheObject> {
  return typeof window === 'undefined' ? createInertApolloClient() : getApolloClient();
}
